import { NavigationStepperItemType } from './NavigationStepper';
import { useMemo, useState } from 'react';

interface useNavigationStepperProps {
	steps: NavigationStepperItemType[];
	canSkip?: boolean;
	activeKey?: string;
}

export default function useNavigationStepper(props: useNavigationStepperProps) {
	const { steps, canSkip, activeKey } = props;

	const loopRouter = (
		steps: NavigationStepperItemType[],
		parentKey?: string,
	): NavigationStepperItemType[] => {
		let router: any = {};

		steps.map((step, index, arr) => {
			const finalKey = parentKey ? `${parentKey}.${step.key}` : step.key;

			if (step?.children?.length) {
				const subRouters = loopRouter(step.children, finalKey);

				router = {
					...router,
					...subRouters,
				};
			}

			// todo: add children

			if (typeof arr[index - 1] !== 'undefined') {
				step.prevStepKey = parentKey
					? `${parentKey}.${arr[index - 1].key}`
					: arr[index - 1].key;
			}

			if (typeof arr[index + 1] !== 'undefined') {
				step.nextStepKey = parentKey
					? `${parentKey}.${arr[index + 1].key}`
					: arr[index + 1].key;
			}

			router[finalKey] = step;
		});

		return router;
	};

	const loopSteps = (
		steps: NavigationStepperItemType[],
		parentKey?: string,
	) => {
		let doDisabled = false;

		return steps.map((step, index, arr) => {
			if (step.key === activeKey) {
				step.disabled = false;
				doDisabled = true;
			} else {
				if (!canSkip && doDisabled) {
					step.disabled = true;
				}
			}

			return step;
		});
	};

	const routes: NavigationStepperItemType[] = useMemo(() => {
		return loopRouter(steps);
	}, [steps]);

	const transformedSteps = useMemo(() => {
		return loopSteps(steps);
	}, [steps, activeKey]);

	const loopStepPaths = (
		paths: string[],
		tempSteps: NavigationStepperItemType[],
	): NavigationStepperItemType | null => {
		const firstPath = paths.shift();

		const step = tempSteps.find(tempStep => tempStep.key === firstPath);

		if (!step) {
			return null;
		}

		if (paths.length && step?.children?.length) {
			return loopStepPaths(paths, step.children);
		}

		return step;
	};

	const findActiveStep = (path: string) => {
		const paths = path.split('.');

		if (!paths.length) {
			return null;
		}

		return loopStepPaths(paths, steps);
	};

	const findPrevStepPath = (path: string): string | undefined => {
		const route = routes.find(r => r.fullStepKey === path);

		if (!route) {
			return undefined;
		}

		return route.prevStepKey;
	};

	const findNextStepPath = (path: string): string | undefined => {
		let nextStepKey = undefined;

		if (Array.isArray(routes)) {
			const route = routes.find(r => r.fullStepKey === path);
			nextStepKey = route?.nextStepKey;
		} else {
			const activeRoute = routes[path];
			if (activeRoute) {
				nextStepKey = activeRoute?.nextStepKey;
			}
		}

		return nextStepKey;
	};

	return {
		findActiveStep,
		findNextStepPath,
		findPrevStepPath,
		transformedSteps,
		routes,
	};
}
